import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkHasFilters } from '@/utils/helpers';

export type UseFiltersType<T> = {
  filters: T;
  hasFilters: boolean;
  updateFilters: (filter: T[keyof T], type: keyof T) => void;
  clearFilters: () => void;
  setFilters?: (value: T) => void;
};

export const useFilters = <T extends Record<string, unknown>>(
  defaultState: T,
  disabledKeys?: (keyof T)[],
  initialState?: T,
): Required<UseFiltersType<T>> => {
  const defaultStateRef = useRef<T>(defaultState);
  const [filters, setFiltersState] = useState<T>(initialState ?? defaultState);

  useEffect(() => {
    defaultStateRef.current = defaultState;
  }, [defaultState]);

  const hasFilters = useMemo(
    () => checkHasFilters(filters, defaultStateRef.current, disabledKeys),
    [disabledKeys, filters],
  );

  const updateFilters = useCallback((filter: T[keyof T], type: keyof T) => {
    if (!type) return;

    setFiltersState((prevState) => ({
      ...prevState,
      [type]: filter,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(defaultStateRef.current);
  }, []);

  const setFilters = useCallback((value: T) => {
    setFiltersState(value);
  }, []);

  return {
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    setFilters,
  };
};
